import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, CheckCircle2, Phone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SERVICES, BUSINESS } from "@/data/site";
import { submitQuote } from "@/lib/api";
import { SplitLines, Reveal, EASE } from "@/components/motion/Reveal";

const EMPTY = { name: "", phone: "", email: "", suburb: "", service: "", property_type: "Residential", message: "" };

const Field = ({ label, id, children, required }) => (
  <label className="block">
    <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-2">
      {label} {required && <span className="text-brand-light">*</span>}
    </span>
    {children}
  </label>
);

const SuccessPanel = ({ done, onReset }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.6, ease: EASE }}
    className="text-center py-10"
    data-testid="quote-success"
  >
    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.15, type: "spring", stiffness: 200, damping: 14 }} className="mx-auto grid place-items-center w-20 h-20 rounded-full bg-brand text-white mb-6">
      <CheckCircle2 size={40} strokeWidth={2.5} />
    </motion.div>
    <h3 className="h-display text-4xl text-white mb-3">Too easy, {done.name.split(" ")[0]}.</h3>
    <p className="text-slate-400 max-w-sm mx-auto">
      Your <span className="text-white">{done.service}</span> enquiry for <span className="text-white">{done.suburb}</span> is in. Rickie will be in touch shortly.
    </p>
    <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Ref · {done.id.slice(0, 8).toUpperCase()}</div>
    <button onClick={onReset} className="btn-ghost mt-8 !text-base" data-testid="quote-another-btn">
      Send another enquiry
    </button>
  </motion.div>
);

const QuoteFields = ({ form, set, setForm, loading, onSubmit }) => (
  <motion.form onSubmit={onSubmit} exit={{ opacity: 0, y: -10 }} className="grid sm:grid-cols-2 gap-5" data-testid="quote-form" noValidate>
    <Field label="Your name" required>
      <input className="field" value={form.name} onChange={set("name")} placeholder="Rick Sanchez" data-testid="quote-form-name-input" />
    </Field>
    <Field label="Phone" required>
      <input className="field" type="tel" value={form.phone} onChange={set("phone")} placeholder="04xx xxx xxx" data-testid="quote-form-phone-input" />
    </Field>
    <Field label="Email">
      <input className="field" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="quote-form-email-input" />
    </Field>
    <Field label="Suburb" required>
      <input className="field" value={form.suburb} onChange={set("suburb")} placeholder="Strathdale" data-testid="quote-form-suburb-input" />
    </Field>
    <Field label="Service needed" required>
      <select className="field appearance-none" value={form.service} onChange={set("service")} data-testid="quote-form-service-select">
        <option value="" disabled>Choose a service…</option>
        {SERVICES.map((s) => (
          <option key={s.id} value={s.title}>{s.title}</option>
        ))}
        <option value="Multiple / not sure">Multiple / not sure</option>
      </select>
    </Field>
    <Field label="Property type">
      <div className="grid grid-cols-2 gap-2" data-testid="quote-form-property-type">
        {["Residential", "Commercial"].map((t) => (
          <button
            type="button"
            key={t}
            onClick={() => setForm((f) => ({ ...f, property_type: t }))}
            data-testid={`quote-form-property-${t.toLowerCase()}`}
            className={`py-3.5 rounded-md border font-display font-bold uppercase tracking-wider text-sm transition-colors duration-200 ${
              form.property_type === t ? "bg-brand border-brand text-white" : "bg-ink/70 border-white/10 text-slate-300 hover:border-white/30"
            }`}
          >
            {t}
          </button>
        ))}
      </div>
    </Field>
    <div className="sm:col-span-2">
      <Field label="Tell us about the job">
        <textarea className="field min-h-[120px] resize-y" value={form.message} onChange={set("message")} placeholder="e.g. Double driveway with oil stains, plus the front weatherboards have gone green on the shady side…" data-testid="quote-form-message-input" />
      </Field>
    </div>
    <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
      <button type="submit" disabled={loading} className="btn-primary flex-1 disabled:opacity-60 disabled:hover:scale-100" data-testid="quote-form-submit-btn">
        {loading ? <Loader2 className="animate-spin" size={20} /> : <Send size={18} strokeWidth={2.5} />}
        {loading ? "Sending…" : "Send my free quote request"}
      </button>
      <span className="text-xs text-slate-500 sm:max-w-[160px] leading-snug">No spam, no lock-in. Just a price.</span>
    </div>
  </motion.form>
);

export const QuoteForm = ({ preselectedService }) => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (preselectedService) setForm((f) => ({ ...f, service: preselectedService }));
  }, [preselectedService]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.suburb || !form.service) {
      toast.error("Please fill in your name, phone, suburb and the service you need.");
      return;
    }
    setLoading(true);
    try {
      const res = await submitQuote({ ...form, email: form.email || null, message: form.message || null });
      setDone(res);
      toast.success("Quote request sent! Rickie will be in touch shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute right-0 top-0 w-[55%] h-full bg-brand/[0.04] -skew-x-6 translate-x-1/4 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-36">
          <Reveal y={16} className="eyebrow mb-4">Free, fixed-price quote</Reveal>
          <h2 className="h-display text-5xl sm:text-6xl lg:text-7xl text-white">
            <SplitLines lines={["Let's get", "your place", <><span className="text-brand-light">sorted.</span></>]} />
          </h2>
          <Reveal delay={0.25} className="mt-8 text-slate-400 leading-relaxed max-w-md">
            Fill this in and Rickie will call or text you back — usually within the hour during work hours, always within 24. Prefer to talk now?
          </Reveal>
          <Reveal delay={0.35} className="mt-6">
            <a href={`tel:${BUSINESS.phoneRaw}`} className="inline-flex items-center gap-3 group" data-testid="quote-call-link">
              <span className="grid place-items-center w-12 h-12 rounded-full bg-brand text-white group-hover:scale-110 transition-transform duration-300">
                <Phone size={20} strokeWidth={2.5} />
              </span>
              <span className="font-display font-black text-3xl sm:text-4xl text-white tracking-tight">{BUSINESS.phone}</span>
            </a>
            <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">{BUSINESS.hours}</div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-7">
          <div className="relative bg-panel/90 border border-white/10 rounded-2xl p-6 sm:p-10 shadow-[0_40px_80px_rgba(0,0,0,0.4)]">
            <div className="absolute -top-px left-10 right-10 h-px bg-gradient-to-r from-transparent via-brand to-transparent" />
            <AnimatePresence mode="wait">
              {done ? (
                <SuccessPanel key="done" done={done} onReset={() => { setDone(null); setForm(EMPTY); }} />
              ) : (
                <QuoteFields key="form" form={form} set={set} setForm={setForm} loading={loading} onSubmit={onSubmit} />
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
